import React, { PureComponent } from 'react';
import Rollbar from 'rollbar';
import { connect } from 'react-redux';
import { retrieveCreditCardValues } from '@widergy/web-utils/lib/form';
import { isEmpty } from '@widergy/web-utils/lib/array';
import { push } from 'connected-react-router';
import { getIn } from 'seamless-immutable';
import { string, number, bool, shape, arrayOf } from 'prop-types';
import { submit } from 'redux-form';
import { AlertHandler } from '@widergy/energy-ui';

import { ROUTES } from 'constants/routes';
import SecondaryTopbar from 'app/components/SecondaryTopbar';
import { actionCreators as paymentActions } from 'redux/paymentHandlers';
import { appConfig } from 'config/customization';
import { PAYMENT_GATEWAYS } from 'constants/payments';
import { message } from 'utils/stringUtils';
import { generateFDFormData } from 'utils/paymentUtils';
import { PAYMENT_FORM } from 'constants/reduxForms';
import { UTILITIES } from 'constants/utilitiesMappers';
import { accountTypes } from 'types/accountTypes';
import { accountUnpaidType } from 'types/paymentTypes';
import { emailField } from 'constants/fields';

import Decidir from './components/Decidir';
import FirstData from './components/FirstData';
import styles from './styles.module.scss';

class PaymentRequest extends PureComponent {
  constructor(props) {
    super(props);
    this.formRef = React.createRef();
    this.state = { hiddenInputs: [] };
  }

  componentDidMount() {
    if (!this.props.paymentInProgress) this.props.dispatch(push(ROUTES.ACCOUNT_STATUS));
    else if (appConfig.mappers === UTILITIES.edes) this.validateBarCode();
  }

  validateBarCode = () => {
    if (this.props.paymentDetails.some(detail => !detail.barcode)) {
      Rollbar.error('payment barcode error', { details: this.props.paymentDetails });
      AlertHandler.error(message('DefaultMessages:getAccountUnpaidFailure'));
      this.handleCancelPayment();
    }
  };

  handleDecidirPayment = async values => {
    const {
      paymentDetails,
      totalAmount,
      procedureId,
      dispatch,
      isPublic,
      decidirApiKey,
      decidirUrl
    } = this.props;
    const newValues = { ...retrieveCreditCardValues(values, null, 'cardNumber') };
    await dispatch(
      paymentActions.getPaymentToken(
        paymentDetails,
        newValues,
        totalAmount,
        procedureId,
        decidirApiKey,
        decidirUrl
      )
    );
    dispatch(push(isPublic ? ROUTES.PUBLIC_PAYMENTS : ROUTES.ACCOUNT_STATUS));
  };

  handleCancelPayment = () => {
    const { dispatch, isPublic } = this.props;
    dispatch(push(isPublic ? ROUTES.PUBLIC_PAYMENT_DOCUMENTS : ROUTES.ACCOUNT_STATUS));
    dispatch(paymentActions.paymentFlowCancel());
  };

  debtIsValid = async () => {
    await this.props.dispatch(paymentActions.getAccountUnpaid(this.props.accountUnpaid[0], true));
    if (isEmpty(this.props.accountUnpaid)) return false;
    const { debts } = this.props.accountUnpaid[0];
    return this.props.paymentDetails.every(_detail =>
      debts.find(
        _debt =>
          (_debt.barcode === _detail.barcode || _debt.external_id === _detail.external_id) &&
          _debt.balance === _detail.amount
      )
    );
  };

  handleFDPayment = async values => {
    const { dispatch, currentAccount, accountUnpaid, email } = this.props;

    let emailValue = values[emailField.key];
    if (!emailValue && appConfig.payments.paymentsWithEmail) emailValue = email;

    const newValues = {
      ...retrieveCreditCardValues(values, null, 'cardNumber'),
      cardFunction: values.cardFunction && values.cardFunction.split('_')[1]
    };

    const isValid = await this.debtIsValid();
    if (isValid) {
      await dispatch(
        paymentActions.getPaymentIdentifier(
          PAYMENT_GATEWAYS.FIRST_DATA,
          currentAccount || (!isEmpty(accountUnpaid) && accountUnpaid[0]),
          emailValue
        )
      );
      if (this.props.paymentIdentifier || this.props.paymentIdentifier === 0) {
        const hiddenInputs = generateFDFormData(
          this.props.totalAmount,
          newValues,
          this.props.paymentIdentifier,
          this.props.paymentDetails,
          this.props.agentTransactionId
        );

        this.setState({ hiddenInputs }, () => this.formRef.current.submit());
        Rollbar.info('payment submitted', {
          details: this.props.paymentDetails,
          identifier: this.props.paymentIdentifier,
          amount: this.props.totalAmount
        });
        this.setState({ redirectLoading: true });
        // Deferred setState to show loading until provider redirect is finished.
        setTimeout(() => this.setState({ redirectLoading: false }), 50000);
      } else {
        this.handleCancelPayment();
      }
    } else {
      AlertHandler.error(message('DefaultMessages:getAccountUnpaidFailure'));
      this.handleCancelPayment();
    }
  };

  handlePaymentSubmit = () => this.props.dispatch(submit(PAYMENT_FORM));

  render() {
    const { loading, email, isPublic } = this.props;

    const PaymentConfig = {
      [PAYMENT_GATEWAYS.DECIDIR]: {
        component: Decidir,
        props: {
          onSubmit: this.handleDecidirPayment,
          onCancel: this.handleCancelPayment,
          loading,
          initialValues: { email }
        }
      },
      [PAYMENT_GATEWAYS.FIRST_DATA]: {
        component: FirstData,
        props: {
          onSubmit: this.handleFDPayment,
          onSubmitButtonPress: this.handlePaymentSubmit,
          onCancel: this.handleCancelPayment,
          loading: loading || this.state.redirectLoading,
          formRef: this.formRef,
          hiddenInputs: this.state.hiddenInputs,
          isPublic
        }
      }
    };

    const Config = PaymentConfig[appConfig.paymentFlow.provider];
    return (
      <div className={styles.container}>
        <SecondaryTopbar titleText={message('Payments:modalTitle')} />
        <Config.component {...Config.props} />
      </div>
    );
  }
}

PaymentRequest.propTypes = {
  totalAmount: number.isRequired,
  paymentInProgress: bool.isRequired,
  paymentDetails: shape({ label: string, amount: number }),
  paymentRequestMessage: string,
  loading: bool,
  paymentIdentifier: string,
  procedureId: string,
  isPublic: bool,
  currentAccount: accountTypes,
  accountUnpaid: arrayOf(accountUnpaidType),
  email: string,
  agentTransactionId: string,
  decidirApiKey: string,
  decidirUrl: string
};

const mapStateToProps = store => ({
  email: getIn(store, ['auth', 'currentUser', 'email']),
  paymentInProgress: getIn(store, ['payment', 'paymentFlow', 'paymentInProgress']),
  paymentDetails: getIn(store, ['payment', 'paymentFlow', 'paymentDetails']),
  totalAmount: getIn(store, ['payment', 'paymentFlow', 'totalAmount']),
  procedureId: getIn(store, ['payment', 'paymentFlow', 'procedureId']),
  loading:
    store.payment.paymentTokenLoading ||
    store.payment.paymentRequestLoading ||
    store.payment.paymentIdentifierLoading ||
    store.payment.accountUnpaidLoading,
  paymentIdentifier: store.payment.paymentIdentifier,
  accountUnpaid: store.payment.accountUnpaid,
  currentAccount: getIn(store, ['account', 'currentAccount']),
  agentTransactionId: store.payment.agentTransactionId,
  decidirApiKey: getIn(store, ['utility', 'appConfiguration', 'payment_gateway_api_key']),
  decidirUrl: getIn(store, ['utility', 'appConfiguration', 'payment_gateway_url'])
});

export default connect(mapStateToProps)(PaymentRequest);
